import { registerPlugin } from '@capacitor/core';
import type { BackgroundGeolocationPlugin, Location as BgLocation } from '@capacitor-community/background-geolocation';
import { SocketEvents, type LocationPing } from '@tcrd/shared';
import { api } from './api';
import { env } from './env';
import { realtime } from './realtime';

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');

export interface TrackerStatus {
  running: boolean;
  permission: 'unknown' | 'granted' | 'denied';
  lastFixAt: number | null;
  lastAccuracy: number | null;
  lastSentAt: number | null;
  queued: number;
  online: boolean;
  error: string | null;
}

type Listener = (s: TrackerStatus) => void;
const QUEUE_KEY = (rideId: string) => `tcrd.gps.queue.${rideId}`;
/** Sin movimiento (< 1 m/s) se envía con menos frecuencia para ahorrar batería y datos. */
const STATIONARY_INTERVAL_MS = 15_000;

/**
 * Seguimiento GPS del conductor durante un servicio activo.
 *  - Android: servicio en primer plano con notificación persistente
 *    (sigue funcionando con la pantalla bloqueada si Android lo permite).
 *  - Web: watchPosition (solo mientras la pestaña está abierta).
 * Los puntos se envían por Socket.IO; si no hay conexión se encolan
 * (también en almacenamiento local) y se sincronizan por lotes al volver.
 */
class RideTracker {
  private rideId: string | null = null;
  private intervalMs = 4000;
  private watcherId: string | number | null = null;
  private queue: LocationPing[] = [];
  private lastQueuedAt = 0;
  private flushing = false;
  private listeners = new Set<Listener>();
  private retryTimer: ReturnType<typeof setInterval> | null = null;
  private status: TrackerStatus = { running: false, permission: 'unknown', lastFixAt: null, lastAccuracy: null, lastSentAt: null, queued: 0, online: navigator.onLine, error: null };

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    fn(this.status);
    return () => this.listeners.delete(fn);
  }

  private set(patch: Partial<TrackerStatus>) {
    this.status = { ...this.status, ...patch, queued: this.queue.length };
    this.listeners.forEach((l) => l(this.status));
  }

  get activeRideId() {
    return this.rideId;
  }

  async start(rideId: string, intervalSeconds: number) {
    if (this.rideId === rideId && this.status.running) return;
    await this.stop();
    this.rideId = rideId;
    this.intervalMs = Math.max(2, intervalSeconds) * 1000;
    try {
      this.queue = JSON.parse(localStorage.getItem(QUEUE_KEY(rideId)) ?? '[]');
    } catch {
      this.queue = [];
    }
    this.set({ running: true, error: null });
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    this.retryTimer = setInterval(() => void this.flush(), 10_000);

    if (env.isNative) {
      this.watcherId = await BackgroundGeolocation.addWatcher(
        {
          backgroundTitle: 'TuConductorRD: viaje en curso',
          backgroundMessage: 'Compartiendo tu ubicación con el cliente durante este servicio.',
          requestPermissions: true,
          stale: false,
          // 0: también reporta parado (esperando al cliente). Con 5 m no llegaban puntos y el cliente veía "Sin señal".
          // onFix limita el envío a cada 15 s cuando el conductor no se mueve.
          distanceFilter: 0,
        },
        (loc?: BgLocation, err?: { code?: string; message?: string }) => {
          if (err) {
            if (err.code === 'NOT_AUTHORIZED') this.set({ permission: 'denied', error: 'Permiso de ubicación denegado. Actívalo como "Permitir siempre" o "Mientras se usa la app".' });
            else this.set({ error: err.message ?? 'Error de GPS' });
            return;
          }
          if (loc) this.onFix({ lat: loc.latitude, lng: loc.longitude, accuracy: loc.accuracy, heading: loc.bearing, speed: loc.speed, time: loc.time ?? Date.now() });
        },
      );
    } else if ('geolocation' in navigator) {
      this.watcherId = navigator.geolocation.watchPosition(
        (pos) => this.onFix({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, heading: pos.coords.heading, speed: pos.coords.speed, time: pos.timestamp }),
        (e) => this.set({ permission: e.code === e.PERMISSION_DENIED ? 'denied' : this.status.permission, error: e.code === e.PERMISSION_DENIED ? 'Permiso de ubicación denegado en el navegador.' : 'No se pudo obtener la ubicación.' }),
        { enableHighAccuracy: true, maximumAge: 2000, timeout: 20_000 },
      );
    } else {
      this.set({ error: 'Este dispositivo no ofrece geolocalización.' });
    }
  }

  async stop() {
    if (this.watcherId !== null) {
      if (env.isNative) await BackgroundGeolocation.removeWatcher({ id: String(this.watcherId) }).catch(() => undefined);
      else navigator.geolocation.clearWatch(this.watcherId as number);
    }
    this.watcherId = null;
    if (this.retryTimer) clearInterval(this.retryTimer);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    if (this.rideId && this.queue.length) await this.flush();
    if (this.rideId) localStorage.removeItem(QUEUE_KEY(this.rideId));
    this.rideId = null;
    this.queue = [];
    this.set({ running: false });
  }

  async openSettings() {
    if (env.isNative) await BackgroundGeolocation.openSettings();
  }

  private onOnline = () => {
    this.set({ online: true });
    void this.flush();
  };
  private onOffline = () => this.set({ online: false });

  private onFix(f: { lat: number; lng: number; accuracy: number; heading: number | null; speed: number | null; time: number }) {
    this.set({ permission: 'granted', lastFixAt: f.time, lastAccuracy: f.accuracy, error: null });
    const moving = (f.speed ?? 0) >= 1;
    const minGap = moving ? this.intervalMs : STATIONARY_INTERVAL_MS;
    if (f.time - this.lastQueuedAt < minGap) return;
    this.lastQueuedAt = f.time;
    this.queue.push({
      lat: f.lat,
      lng: f.lng,
      accuracy: Math.round(f.accuracy * 10) / 10,
      heading: f.heading !== null && Number.isFinite(f.heading) && f.heading >= 0 ? f.heading : null,
      speed: f.speed !== null && Number.isFinite(f.speed) && f.speed >= 0 ? f.speed : null,
      recordedAt: new Date(f.time).toISOString(),
    });
    if (this.queue.length > 1000) this.queue.splice(0, this.queue.length - 1000);
    this.persist();
    void this.flush();
  }

  private persist() {
    if (!this.rideId) return;
    try {
      localStorage.setItem(QUEUE_KEY(this.rideId), JSON.stringify(this.queue));
    } catch {
      /* almacenamiento lleno: se conserva en memoria */
    }
    this.set({});
  }

  private async flush() {
    if (this.flushing || !this.rideId || !this.queue.length) return;
    this.flushing = true;
    const batch = this.queue.slice(0, 300);
    try {
      let ok = false;
      if (batch.length === 1 && realtime.connected) {
        const ack = await realtime.emit<{ ok: boolean; code?: string }>(SocketEvents.sendLocation, { rideId: this.rideId, point: batch[0] }, 5000);
        ok = !!ack?.ok;
        if (ack?.code === 'TRACKING_NOT_ACTIVE') ok = true; // el viaje ya terminó: descartar
      }
      if (!ok) {
        await api(`/rides/${this.rideId}/locations`, { body: { points: batch } });
        ok = true;
      }
      if (ok) {
        this.queue.splice(0, batch.length);
        this.persist();
        this.set({ lastSentAt: Date.now(), online: true });
      }
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code === 'TRACKING_NOT_ACTIVE') {
        this.queue = [];
        this.persist();
      } else this.set({ online: code !== 'NETWORK_ERROR' ? this.status.online : false });
    } finally {
      this.flushing = false;
    }
    if (this.queue.length && this.status.online) setTimeout(() => void this.flush(), 500);
  }
}

export const rideTracker = new RideTracker();
